/**
 * Cookie utility functions for persisting character data across page refreshes
 */

/**
 * Set a cookie with the specified name and value
 * @param name - Cookie name
 * @param value - Cookie value
 * @param days - Number of days until expiration (default: 30 days)
 */
export function setCookie(name: string, value: string, days: number = 30): void {
  try {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    const expiresString = expires.toUTCString();

    // Create cookie string with security flags
    let cookieString = `${name}=${encodeURIComponent(value)};expires=${expiresString};path=/;SameSite=Lax`;

    // Add secure flag if on HTTPS
    if (window.location.protocol === 'https:') {
      cookieString += ';Secure';
    }

    document.cookie = cookieString;
  } catch (error) {
    console.warn('Failed to set cookie:', error);
  }
}

/**
 * Get the value of a cookie by name
 * @param name - Cookie name
 * @returns Cookie value or null if not found
 */
export function getCookie(name: string): string | null {
  try {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');

    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return decodeURIComponent(c.substring(nameEQ.length, c.length));
    }

    return null;
  } catch (error) {
    console.warn('Failed to get cookie:', error);
    return null;
  }
}

/**
 * Delete a cookie by name
 * @param name - Cookie name
 */
export function deleteCookie(name: string): void {
  try {
    setCookie(name, '', -1);
  } catch (error) {
    console.warn('Failed to delete cookie:', error);
  }
}

/**
 * Save the active character ID to a cookie
 * @param characterId - Character ID to save
 */
export function saveActiveCharacterId(characterId: string): void {
  console.log('🍪 Saving character ID to cookie:', characterId);
  setCookie('activeCharacterId', characterId);
  // Verify it was saved
  const saved = getCookie('activeCharacterId');
  console.log('🍪 Cookie saved, verification:', saved);
}

/**
 * Get the active character ID from cookie
 * @returns Character ID or null if not found
 */
export function getActiveCharacterId(): string | null {
  const id = getCookie('activeCharacterId');
  console.log('🍪 Getting character ID from cookie:', id);
  return id;
}

/**
 * Clear the active character ID cookie
 */
export function clearActiveCharacterId(): void {
  deleteCookie('activeCharacterId');
}
